import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isApiError } from './api/client';
import { SessionProvider } from './app/session';
import { ReauthProvider } from './app/reauth';
import { App } from './app/App';
import { ToastProvider } from './components/Toast';
import { reloadOnceForChunkError } from './components/ErrorBoundary';
import { readStorage } from './lib/hooks';
import { capturePrerendered } from './prerender/boot';
import { installTranslationGuard } from './lib/translationGuard';
import { guessLocale, loadLocale } from './i18n';
// IBM Plex Sans, self-hosted (no Google Fonts: no visitor IP to a third party). Latin subset; Chinese falls back to system fonts.
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-700.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/pages.css';

/** Apply the remembered theme before React renders to avoid a light flash in dark mode. */
function applyStoredPrefs() {
  const theme = readStorage('btw.theme');
  if (theme === 'light' || theme === 'dark') document.documentElement.setAttribute('data-theme', theme);
  if (readStorage('btw.largeText') === '1') document.documentElement.classList.add('large-text');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Client errors (4xx) are answers, not glitches: don't retry them.
      retry: (count, error) => (isApiError(error) && error.status >= 400 && error.status < 500 ? false : count < 2),
    },
    mutations: { retry: false },
  },
});

/** Prerendered pages carry /api/features: start with it (marked stale, so it is fetched again right away). */
function seedFromPrerender(qc: QueryClient) {
  const el = document.getElementById('btw-boot');
  if (!el?.textContent) return;
  try {
    const boot = JSON.parse(el.textContent) as { features?: unknown };
    if (boot.features) qc.setQueryData(['features'], boot.features, { updatedAt: 0 });
  } catch {
    /* ignore */
  }
}

async function start() {
  applyStoredPrefs();
  // Browser page translation must not crash React (bug 2026-10-28, onboarding in a machine-translated tab).
  installTranslationGuard();
  // After a deploy an open tab may ask for chunks that are gone: reload once to pick up the new build (AR-13).
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForChunkError()) event.preventDefault();
  });
  // Dev-only fixture mode (never active in production builds unless VITE_DEMO=1 is set explicitly).
  if (import.meta.env.VITE_DEMO === '1') {
    const { installDemo } = await import('./demo/install');
    installDemo();
  }
  // The starting language before the first render (English and Chinese are separate chunks; English pages under /en).
  await Promise.all([loadLocale(guessLocale()), location.pathname.startsWith('/en') ? loadLocale('en') : null]).catch(() => undefined);
  const root = document.getElementById('root')!;
  capturePrerendered(root);
  seedFromPrerender(queryClient);
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <ToastProvider>
              <ReauthProvider>
                <App />
              </ReauthProvider>
            </ToastProvider>
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

start();
